import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Shield, Loader2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { useTranslation } from '@/i18n/TranslationContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState('email'); // email, code, done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setStep('code');
      toast.success('Reset code sent', { description: 'Check your email for the 6-digit code.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send reset code';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Please enter the full 6-digit code'); return; }
    if (!newPassword) { setError('Please enter a new password'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.resetPassword(email.trim().toLowerCase(), code, newPassword);
      setStep('done');
      toast.success('Password reset!', { description: 'You can now log in with your new password.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to reset password';
      setError(msg);
      toast.error('Reset failed', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background vrs-grid relative flex items-center justify-center p-6">
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <LanguageToggle />
        <ThemeToggle />
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(600px circle at 50% 40%, rgba(26,209,230,0.08), transparent 55%)' }}
      />
      <Card className="w-full max-w-[440px] border-border/60 bg-card/80 backdrop-blur-sm shadow-[var(--vrs-shadow-elev-2)] relative">
        <CardHeader className="space-y-1 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span className="text-lg font-bold tracking-tight text-foreground">VRS</span>
          </div>
          <CardTitle className="text-xl font-semibold tracking-tight">
            {step === 'email' && 'Forgot your password?'}
            {step === 'code' && 'Reset your password'}
            {step === 'done' && 'Password reset!'}
          </CardTitle>
          <CardDescription>
            {step === 'email' && 'Enter your email and we\'ll send you a reset code.'}
            {step === 'code' && `Enter the 6-digit code sent to ${email}`}
            {step === 'done' && 'Your password has been changed successfully.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">{t('email_address')}</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  data-testid="forgot-email-input"
                  className="h-11 bg-muted/30 border-border"
                  autoFocus
                />
              </div>
              {error && <p data-testid="forgot-error-text" className="text-sm text-destructive">{error}</p>}
              <Button type="submit" data-testid="forgot-send-code-button" className="w-full h-11 font-semibold" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : <><Mail className="mr-2 h-4 w-4" />{t('send_reset_code')}</>}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => navigate('/login')}>
                <ArrowLeft className="mr-2 h-4 w-4" />Back to login
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label>Verification code</Label>
                <div className="flex justify-center" data-testid="forgot-otp-input">
                  <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); setError(''); }}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg border-border bg-muted/30" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('new_password')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  data-testid="forgot-new-password-input"
                  className="h-11 bg-muted/30 border-border"
                />
              </div>
              {error && <p data-testid="forgot-error-text" className="text-sm text-destructive">{error}</p>}
              <Button type="submit" data-testid="forgot-reset-button" className="w-full h-11 font-semibold" disabled={loading || code.length !== 6}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</> : 'Reset password'}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => { setStep('email'); setCode(''); setError(''); }}>
                <ArrowLeft className="mr-2 h-4 w-4" />Use a different email
              </Button>
            </form>
          )}

          {step === 'done' && (
            <div className="space-y-4">
              <Button className="w-full h-11 font-semibold" onClick={() => navigate('/login')} data-testid="forgot-back-to-login">
                Sign in with new password
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
